import http from "@/http";
import { ContextController } from "@/view-switch/context-controller";
import { ExpandableSettings } from "@/expandable";
import { AbstractFormControl } from "@/form";
import { TreeSelectHydrator } from "./tree-select-hydrator";
import { TreeItemSettings } from "./tree-item";
import { Subselect, TreeSelection } from "./tree-select";

export interface TreeSelectControllerSettings {
    /** Optionally send requests to this endpoint instead of the current page url */
    endpoint?: string;

    /** True when tree-select should use multi selection mode with checkbox items. */
    multiple?: boolean;

    /** Used to identify form controls, adapted for use as subselects in the tree navigation */
    controlClass: string;

    /** Used to identify item subselects within the tree */
    itemSelectClass: string;

    /** Used to identify individual items within an item select */
    itemClass: string;

    /** Used to configure individual tree items */
    itemSettings: TreeItemSettings;

    /** Used to identify the list element within an expandable tree item */
    listClass: string;

    /** Used to configure the expandable list within an expandable tree item */
    listSettings: ExpandableSettings;
}

export abstract class AbstractTreeSelectController extends AbstractFormControl<HTMLElement> {

    static outlets = ['context'];
    
    declare protected contextOutlet: ContextController<TreeSelectControllerSettings>;

    protected settings!: TreeSelectControllerSettings;

    protected hydrator: TreeSelectHydrator|null = null;

    protected initializeHydrator(): void {
        this.settings = this.contextOutlet.contextData;
        this.hydrator = new TreeSelectHydrator(this.settings);
    }

    getName(): string {
        const name = this.element.getAttribute('name');
        if (!name) {
            throw new Error('Tree select requires a name attribute');
        }

        return name;
    }

    disconnect(): void {
        this.hydrator = null;
        super.disconnect();
    }

    protected query(formData: FormData, errorMessage: string) {
        const endpoint = http.resolveUrl(this.settings.endpoint);
        const request = http.postTo(endpoint, formData);
        return http.withToastOnError(request, errorMessage);
    }

    protected loadSubselect<TExpected extends Subselect<unknown>>(
        context: TreeSelection,
        isExpected: (subselect: Subselect<unknown>) => subselect is TExpected,
    ): Promise<TExpected | null> {
        const formData = this.createFormData(context.path.values, 'subselect');

        return this.query(formData, 'Failed to load subitems')
            .then(http.asText)
            .then((subselectHtml: string) => {
                const subselect = this.hydrator?.hydrateSubselect(context, subselectHtml) ?? null;
                if (!subselect || !isExpected(subselect)) {
                    return null;
                }

                return subselect;
            });
    }

    protected createFormData(branch: string[], query: string): FormData {
        const formData = new FormData();
        formData.append('component', 'tree-select');
        formData.append('tree', this.getName());
        formData.append('query', query);
        // TODO: use "branch" terminology consistently across the codebase, instead of "path" or "selection"
        formData.append('path', JSON.stringify(branch));
        return formData;
    }
}

